import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useLanguage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { loginAction, recoveryPasswordAction } from 'src/services/auth';
import { Button, LangSelect, InputWithLabel } from 'src/components/general';
import styles from './authPageStyle.module.scss';

export const AuthPage = () => {
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState('');
  const [disabledInputs, setDisabledInputs] = useState(false);
  const [disabledAuth, setDisabledAuth] = useState(
    !email.trim() || !password.trim(),
  );
  const [disabledRecovery, setDisabledRecovery] = useState(!email.trim());
  const dispatch = useAppDispatch();
  const { t } = useLanguage();

  useEffect(() => {
    setDisabledAuth(!email.trim() || !password.trim() || disabledInputs);
    setDisabledRecovery(!email.trim() || disabledInputs);
  }, [email, password, disabledInputs]);

  const onLogin = useCallback(async () => {
    if (disabledAuth) {
      return;
    }

    setDisabledInputs(true);
    const response = await dispatch(
      loginAction({ email: email.trim(), password: password.trim() }),
    );
    setDisabledInputs(false);
    if (loginAction.rejected.match(response)) {
      setError('Error');
    }
  }, [disabledAuth, dispatch, email, password]);

  const onRestore = useCallback(async () => {
    if (disabledRecovery) {
      return;
    }

    setDisabledInputs(true);
    const response = await dispatch(
      recoveryPasswordAction({ data: { email: email.trim() } }),
    );
    setDisabledInputs(false);
    if (recoveryPasswordAction.fulfilled.match(response)) {
      setError('');
      setRecoveryMode(false);
    } else {
      setError('Error');
    }
  }, [disabledRecovery, dispatch, email]);

  return (
    <div className={styles.container}>
      <div className={styles.leftSection}>Admin VIAVR</div>
      <div className={styles.rightSection}>
        <div className={styles.header}>
          <LangSelect className={styles.lang} design="switcherBorder" />
        </div>
        <div className={styles.content}>
          <div className={styles.label}>Admin VIAVR</div>
          {recoveryMode ? (
            <div className={styles.fields}>
              <div className={styles.recoveryLabel}>
                {t('page.auth.recoveryPass')}
              </div>
              <div className={styles.recoveryDescription}>
                {t('page.auth.description')}
              </div>
              <InputWithLabel
                key="email"
                label="Email"
                type="text"
                inputMode="email"
                enterKeyHint="done"
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setError('');
                  setEmail(event.target.value);
                }}
                className={styles.field}
                disabled={disabledInputs}
                error={error}
                onEnter={onRestore}
              />
              <Button
                key="submit"
                label={t('page.auth.restore')}
                design="lightBlue"
                onClick={onRestore}
                className={styles.button}
                disabled={disabledRecovery}
              />
              <Button
                key="returnToLogin"
                label={t('page.auth.returnToLogin')}
                design="backLink"
                className={styles.button}
                onClick={() => {
                  setError('');
                  setRecoveryMode(false);
                }}
              />
            </div>
          ) : (
            <div className={styles.fields}>
              <InputWithLabel
                key="email"
                label="Email"
                type="text"
                inputMode="email"
                enterKeyHint="next"
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setError('');
                  setEmail(event.target.value);
                }}
                className={styles.field}
                disabled={disabledInputs}
                error={error}
                tabIndex={1}
              />
              <InputWithLabel
                label={t('page.auth.password')}
                type="password"
                enterKeyHint="done"
                value={password}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setError('');
                  setPassword(event.target.value);
                }}
                className={styles.passField}
                disabled={disabledInputs}
                error={error}
                onEnter={onLogin}
                tabIndex={2}
              />
              <Button
                key="submit"
                label={t('page.auth.login')}
                design="lightBlue"
                onClick={onLogin}
                className={styles.button}
                disabled={disabledAuth}
              />
              <Button
                key="forgotPassword"
                label={t('page.auth.forgotPassword')}
                design="link"
                className={styles.button}
                onClick={() => {
                  setError('');
                  setRecoveryMode(true);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
